import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Modal,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  IconButton,
  CircularProgress,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import AddIcon from '@mui/icons-material/Add';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { useNavigate, Navigate } from 'react-router';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import {
  useListSalesQuery,
  useStartOrResumePickingMutation,
  useCreateManualPickingMutation,
  useListPickingWarehousesQuery,
  useMarkSaleDeletedMutation,
  useMarkSpickingDeletedMutation,
} from '../../store/api/pickingApi';
import { useAuth } from '../../hooks/useAuth';
import routes from '../../routes/routes';
import { dateInputToday, formatDateDisplayDots } from '../../helpers/dateHelpers';
import {
  loadPickingListFilters,
  savePickingListFilters,
} from '../../helpers/pickingListFiltersStorage';
import BarcodeCameraDialog from './BarcodeCameraDialog';
import SaleDocumentCard from './SaleDocumentCard';
import MarkDeletedDialog, { formatDeletedDocumentLabel } from '../ui/MarkDeletedDialog';
import WarehouseSelectDialog from '../stocktake/WarehouseSelectDialog';
import {
  TERMINAL_ALPHANUM_KEYBOARD_LAYOUT,
  TERMINAL_ALPHANUM_KEYBOARD_DISPLAY,
} from '../../config/terminalKeyboardLayouts';
import { documentCardSx, terminalBarIconButtonSx, terminalSelectSx, terminalSelectMenuProps } from '../../theme/terminalStyles';

const STATUS_FILTER = {
  ALL: 'all',
  IN_PROGRESS: 'in_progress',
  COLLECTED: 'collected',
  DELETED: 'deleted',
};

const getPickingStatus = (sale) => {
  const list = [...(sale.spickings || [])].sort((a, b) => a.id - b.id);
  const sp = list[0];
  if (sale.isCollected || sp?.isDone) return 'collected';
  if (sale.isUpdatedInERP) return 'updated';
  if (sp && !sp.isDone) return 'resume';
  if (!sp) return 'fresh';
  return 'done';
};

const isCollectedStatus = (sale) => getPickingStatus(sale) === 'collected';

const isInProgressStatus = (sale) => {
  const s = getPickingStatus(sale);
  return s === 'fresh' || s === 'updated' || s === 'resume';
};

const statusColor = (sale) => {
  const s = getPickingStatus(sale);
  if (s === 'updated') return '#9c27b0';
  if (s === 'fresh') return '#9e9e9e';
  if (s === 'resume') return '#fff59d';
  if (s === 'collected' || s === 'done') return '#2e7d32';
  return '#9e9e9e';
};

const triggerDatePicker = (input) => {
  if (!input?.showPicker) return;
  try {
    input.showPicker();
  } catch (e) {
    /* ignore */
  }
};

const openDatePicker = (event) => {
  triggerDatePicker(event.currentTarget.querySelector('input[type="date"]'));
};

const filterFormControlSx = {
  ...terminalSelectSx,
  flexShrink: 0,
  minWidth: { xs: 118, sm: 132 },
  '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
  '& .MuiSelect-select, & .MuiOutlinedInput-input': {
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
    py: 0.75,
    fontWeight: 500,
  },
};

const statusFilterControlSx = {
  ...filterFormControlSx,
  ml: 'auto',
  flex: '1 1 0%',
  flexShrink: 1,
  minWidth: 0,
  maxWidth: '100%',
  '& .MuiOutlinedInput-root': {
    minWidth: 0,
    width: '100%',
  },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'clip',
    display: 'block',
    maskImage: 'linear-gradient(to right, #000 calc(100% - 12px), transparent 100%)',
    WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 12px), transparent 100%)',
  },
};

/** Мобільний Chrome/Brave: нативна іконка date перекриває текст білим блоком */
const dateFilterInputSx = {
  width: '100%',
  position: 'relative',
  fontWeight: 500,
  '& .MuiOutlinedInput-input': {
    width: '100%',
    minWidth: '10ch',
    px: 1.75,
    py: 0.75,
    fontVariantNumeric: 'tabular-nums',
    color: 'transparent',
    caretColor: 'transparent',
  },
  '& input[type="date"]::-webkit-datetime-edit': {
    padding: 0,
    margin: 0,
    visibility: 'hidden',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    opacity: 0,
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    cursor: 'pointer',
    background: 'transparent',
  },
};

const dateFilterDisplaySx = {
  position: 'absolute',
  left: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  fontSize: { xs: '0.75rem', sm: '0.875rem' },
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  color: 'text.primary',
  fontWeight: 500,
};

const readInitialListFilters = () => {
  const saved = loadPickingListFilters();
  const today = dateInputToday();
  return {
    dateFrom: saved?.dateFrom ?? today,
    dateTo: saved?.dateTo ?? today,
    statusFilter: saved?.statusFilter ?? STATUS_FILTER.ALL,
  };
};

const SalePickingListPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;

  const [dateFrom, setDateFrom] = useState(() => readInitialListFilters().dateFrom);
  const [dateTo, setDateTo] = useState(() => readInitialListFilters().dateTo);
  const [statusFilter, setStatusFilter] = useState(() => readInitialListFilters().statusFilter);

  useEffect(() => {
    savePickingListFilters({ dateFrom, dateTo, statusFilter });
  }, [dateFrom, dateTo, statusFilter]);
  const [appliedFilter, setAppliedFilter] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [cameraSearchOpen, setCameraSearchOpen] = useState(false);
  const [confirmSale, setConfirmSale] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);

  const deletedOnly = statusFilter === STATUS_FILTER.DELETED;

  const queryParams = useMemo(
    () => ({
      dateFrom,
      dateTo,
      showCollected: '1',
      ...(deletedOnly ? { deleted: '1' } : {}),
      ...(appliedFilter ? { numberContains: appliedFilter } : {}),
    }),
    [dateFrom, dateTo, appliedFilter, deletedOnly]
  );

  const { data: sales = [], isLoading, isFetching, error } = useListSalesQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const filteredSales = useMemo(() => {
    if (statusFilter === STATUS_FILTER.ALL || statusFilter === STATUS_FILTER.DELETED) return sales;
    if (statusFilter === STATUS_FILTER.COLLECTED) {
      return sales.filter(isCollectedStatus);
    }
    return sales.filter(isInProgressStatus);
  }, [sales, statusFilter]);
  const [startPicking, { isLoading: isStarting }] = useStartOrResumePickingMutation();
  const [createManual, { isLoading: isCreating }] = useCreateManualPickingMutation();
  const [markSaleDeleted, { isLoading: deletingSale }] = useMarkSaleDeletedMutation();
  const [markSpickingDeleted, { isLoading: deletingSpicking }] = useMarkSpickingDeletedMutation();

  const canCreatePickings = Boolean(user?.terminalWorkplace?.createNewPickings);
  const choosePickingWarehouse = Boolean(user?.terminalWorkplace?.choosePickingWarehouse);

  if (!user?.terminalWorkplace?.isOrderPicking) {
    return <Navigate to={routes.root} replace />;
  }

  const dialogText = (sale) => {
    if (!sale) return '';
    const st = getPickingStatus(sale);
    const num = sale.number || String(sale.id);
    if (st === 'fresh') return `Розпочати збірку документа #${num}?`;
    if (st === 'updated') return `Документ #${num} оновлено в ERP. Продовжити збірку?`;
    if (sale.isManual) return `Відкрити ручну збірку #${num}?`;
    if (st === 'resume') return `Продовжити збірку / відкрити документ #${num}?`;
    if (st === 'collected') return `Документ #${num} зібрано. Відкрити для перегляду?`;
    return `Збірку завершено. Відкрити документ #${num}?`;
  };

  const deleteLabel = deleteTarget
    ? formatDeletedDocumentLabel({
        number: deleteTarget.isManual ? deleteTarget.number || deleteTarget.id : deleteTarget.number,
        date: deleteTarget.date,
        useHash: Boolean(deleteTarget.isManual),
      })
    : '';

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError('');
    try {
      if (deleteTarget.isManual) {
        await markSpickingDeleted(deleteTarget.id).unwrap();
      } else {
        await markSaleDeleted(deleteTarget.id).unwrap();
      }
      setDeleteTarget(null);
    } catch (e) {
      setDeleteError(e?.data?.message || 'Не вдалося помітити документ на видалення');
    }
  };

  const handleConfirmStart = async () => {
    if (!confirmSale) return;
    if (confirmSale.isManual) {
      const id = confirmSale.id;
      setConfirmSale(null);
      navigate(`${routes.picking}/m/${id}`, {
        state: { spickingId: confirmSale.spickings?.[0]?.id || id },
      });
      return;
    }
    try {
      const res = await startPicking(confirmSale.id).unwrap();
      setConfirmSale(null);
      navigate(`${routes.picking}/${confirmSale.id}`, {
        state: { spickingId: res.spicking.id },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateManual = async (warehouseId) => {
    try {
      const res = await createManual(warehouseId).unwrap();
      setWarehouseDialogOpen(false);
      setCreateConfirmOpen(false);
      const id = res.spicking?.id;
      if (id) {
        navigate(`${routes.picking}/m/${id}`, { state: { spickingId: id } });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const NumericSearchModal = () => {
    const [localVal, setLocalVal] = useState('');
    const keyboardRef = React.useRef(null);

    useEffect(() => {
      if (searchOpen) setLocalVal(appliedFilter);
    }, [searchOpen, appliedFilter]);

    const apply = () => {
      setAppliedFilter(localVal.trim());
      setSearchOpen(false);
    };

    return (
      <Modal open={searchOpen} onClose={() => setSearchOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '92%',
            maxWidth: 420,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Пошук за номером / штрихкодом
          </Typography>
          <TextField
            fullWidth
            value={localVal}
            onChange={(e) => setLocalVal(e.target.value)}
            margin="normal"
          />
          <Keyboard
            keyboardRef={(r) => {
              keyboardRef.current = r;
            }}
            onChange={setLocalVal}
            layout={TERMINAL_ALPHANUM_KEYBOARD_LAYOUT}
            display={TERMINAL_ALPHANUM_KEYBOARD_DISPLAY}
            onKeyPress={(button) => {
              if (button === '{bksp}') {
                setLocalVal((prev) => {
                  const next = prev.slice(0, -1);
                  keyboardRef.current?.setInput(next);
                  return next;
                });
              }
            }}
          />
          <TerminalOrangeButton
            fullWidth
            sx={{ mt: 1 }}
            startIcon={<PhotoCameraIcon />}
            onClick={() => {
              setSearchOpen(false);
              setCameraSearchOpen(true);
            }}
          >
            Сканувати з камери
          </TerminalOrangeButton>
          <TerminalOrangeButton fullWidth sx={{ mt: 2 }} onClick={apply}>
            OK
          </TerminalOrangeButton>
        </Box>
      </Modal>
    );
  };

  const bottomBar = (
    <>
      <IconButton aria-label="пошук" onClick={() => setSearchOpen(true)} sx={terminalBarIconButtonSx}>
        <SearchIcon />
      </IconButton>
      <IconButton
        aria-label="камера"
        onClick={() => setCameraSearchOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <PhotoCameraIcon />
      </IconButton>
      {appliedFilter ? (
        <IconButton
          aria-label="очистити фільтр"
          onClick={() => setAppliedFilter('')}
          sx={terminalBarIconButtonSx}
        >
          <FilterAltOffIcon />
        </IconButton>
      ) : null}
      {canCreatePickings ? (
        <IconButton
          aria-label="створити"
          onClick={() => setCreateConfirmOpen(true)}
          sx={{ ...terminalBarIconButtonSx, ml: 'auto' }}
        >
          <AddIcon />
        </IconButton>
      ) : null}
    </>
  );

  return (
    <TerminalLayout
      title="Збірка товарів"
      titleIcon={<Inventory2OutlinedIcon />}
      backTo={routes.root}
      bottomBar={bottomBar}
    >
      <BarcodeCameraDialog
        open={cameraSearchOpen}
        onClose={() => setCameraSearchOpen(false)}
        onCode={(text) => {
          setAppliedFilter(String(text || '').trim());
        }}
        title="Сканувати номер / штрихкод"
      />
      <NumericSearchModal />

      <Paper sx={{ ...documentCardSx, mb: 1.5, pt: 1.5, pb: 1, px: 1.5, overflow: 'visible' }} elevation={0}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            width: '100%',
            minWidth: 0,
          }}
        >
          <FormControl
            size="small"
            sx={{
              ...filterFormControlSx,
              minWidth: { xs: 132, sm: 148 },
            }}
            onClick={openDatePicker}
          >
            <InputLabel htmlFor="sale-date-from-filter" shrink>
              З дати
            </InputLabel>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <OutlinedInput
                id="sale-date-from-filter"
                type="date"
                label="З дати"
                notched
                value={dateFrom}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!next) return;
                  setDateFrom(next);
                  if (dateTo && next > dateTo) setDateTo(next);
                }}
                onKeyDown={(e) => e.preventDefault()}
                sx={dateFilterInputSx}
                inputProps={{
                  onClick: (e) => {
                    e.stopPropagation();
                    triggerDatePicker(e.currentTarget);
                  },
                }}
              />
              <Typography component="span" aria-hidden sx={dateFilterDisplaySx}>
                {formatDateDisplayDots(dateFrom)}
              </Typography>
            </Box>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              ...filterFormControlSx,
              minWidth: { xs: 132, sm: 148 },
            }}
            onClick={openDatePicker}
          >
            <InputLabel htmlFor="sale-date-to-filter" shrink>
              По дату
            </InputLabel>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <OutlinedInput
                id="sale-date-to-filter"
                type="date"
                label="По дату"
                notched
                value={dateTo}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!next) return;
                  setDateTo(next);
                  if (dateFrom && next < dateFrom) setDateFrom(next);
                }}
                onKeyDown={(e) => e.preventDefault()}
                sx={dateFilterInputSx}
                inputProps={{
                  onClick: (e) => {
                    e.stopPropagation();
                    triggerDatePicker(e.currentTarget);
                  },
                }}
              />
              <Typography component="span" aria-hidden sx={dateFilterDisplaySx}>
                {formatDateDisplayDots(dateTo)}
              </Typography>
            </Box>
          </FormControl>
          <FormControl size="small" sx={statusFilterControlSx}>
            <InputLabel id="sale-status-filter-label">Статус</InputLabel>
            <Select
              labelId="sale-status-filter-label"
              label="Статус"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              MenuProps={terminalSelectMenuProps}
              fullWidth
            >
              <MenuItem value={STATUS_FILTER.ALL}>Всі</MenuItem>
              <MenuItem value={STATUS_FILTER.IN_PROGRESS}>В роботі</MenuItem>
              <MenuItem value={STATUS_FILTER.COLLECTED}>Зібрані</MenuItem>
              <MenuItem value={STATUS_FILTER.DELETED}>Видалені</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">Не вдалося завантажити список</Typography>
      ) : (
        <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          {filteredSales.map((sale) => (
            <SaleDocumentCard
              key={sale.isManual ? `m-${sale.id}` : `s-${sale.id}`}
              sale={sale}
              pickingStatus={getPickingStatus(sale)}
              statusColor={statusColor(sale)}
              markedDeleted={deletedOnly}
              onStart={deletedOnly ? undefined : () => setConfirmSale(sale)}
              onDelete={
                deletedOnly
                  ? undefined
                  : () => {
                      setDeleteError('');
                      setDeleteTarget(sale);
                    }
              }
            />
          ))}
          {!filteredSales.length ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Документів не знайдено
            </Typography>
          ) : null}
        </Box>
      )}

      {isFetching && !isLoading ? (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Оновлення…
        </Typography>
      ) : null}

      <MarkDeletedDialog
        open={Boolean(deleteTarget)}
        documentLabel={deleteLabel}
        loading={deletingSale || deletingSpicking}
        error={deleteError}
        onClose={() => {
          if (deletingSale || deletingSpicking) return;
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <Dialog open={Boolean(confirmSale)} onClose={() => setConfirmSale(null)}>
        <DialogTitle>Підтвердження</DialogTitle>
        <DialogContent>
          <Typography>{confirmSale ? dialogText(confirmSale) : ''}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setConfirmSale(null)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleConfirmStart} disabled={isStarting}>
            {isStarting ? <CircularProgress size={22} color="inherit" /> : 'Так'}
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={createConfirmOpen} onClose={() => setCreateConfirmOpen(false)}>
        <DialogTitle>Нова збірка</DialogTitle>
        <DialogContent>
          <Typography>Створити ручну збірку?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setCreateConfirmOpen(false)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton
            onClick={() => {
              setCreateConfirmOpen(false);
              if (choosePickingWarehouse) {
                setWarehouseDialogOpen(true);
              } else {
                void handleCreateManual();
              }
            }}
            disabled={isCreating}
          >
            {isCreating ? <CircularProgress size={22} color="inherit" /> : 'Так'}
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <WarehouseSelectDialog
        open={warehouseDialogOpen}
        onClose={() => setWarehouseDialogOpen(false)}
        onConfirm={handleCreateManual}
        isSubmitting={isCreating}
        helperText="Для нової збірки потрібно обрати склад."
        useWarehousesQuery={useListPickingWarehousesQuery}
      />
    </TerminalLayout>
  );
};

export default SalePickingListPage;
